// src/components/kuestion/kb/kbApi.js
//
// The three things the Knowledge Base sends (KB feedback & support contract,
// 18 Sep 2026): an article rating, an article gap, a support ticket. None of
// them requires a token — the KB is readable signed out — but one is sent
// when the tab holds one, so the server can record who asked.
//
// Every request carries X-KB-Session, a client-generated id kept in
// localStorage, so a reader changing their mind about an article overwrites
// their rating rather than adding a second row, and a ticket can be tied to
// the ratings from the same visit.
import { API_ENDPOINTS, CLIENT_SURFACE, CLIENT_VERSION } from '../../../config/apiConfig';

const SESSION_KEY = 'kq-kb-session';
let memorySession = null;

const uuid = () => {
  try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch { /* fall through */ }
  const hex = '0123456789abcdef';
  let out = '';
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) out += '-';
    else if (i === 14) out += '4';
    else if (i === 19) out += hex[(Math.random() * 4) | 8]; // eslint-disable-line no-bitwise
    else out += hex[(Math.random() * 16) | 0]; // eslint-disable-line no-bitwise
  }
  return out;
};

/** The anonymous session id, created on first use and kept for the browser. */
export function kbSessionId() {
  try {
    let v = localStorage.getItem(SESSION_KEY);
    if (!v) { v = uuid(); localStorage.setItem(SESSION_KEY, v); }
    return v;
  } catch {
    if (!memorySession) memorySession = uuid(); // private mode: one per page load
    return memorySession;
  }
}

/** Words the way a person counts them: whitespace-delimited after trimming. */
export const wordCount = (s) => {
  const t = String(s || '').trim();
  return t ? t.split(/\s+/).length : 0;
};

export const SUPPORT_LIMITS = { subjectChars: 120, descriptionWords: 200, gapChars: 1200 };

// One failure shape for the forms to branch on. `kind`:
//   rate_limited  — 429; retryAfter in seconds when the server said
//   too_long      — 422 description_too_long (the client cap should prevent it)
//   unavailable   — 404 (not deployed yet), 5xx, or no network
//   rejected      — any other 4xx
export class KbError extends Error {
  constructor(kind, extra = {}) {
    super(kind);
    this.kind = kind;
    Object.assign(this, extra);
  }
}

async function post(url, body, token) {
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-KB-Session': kbSessionId(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new KbError('unavailable');
  }
  let json = null;
  try { json = await res.json(); } catch { /* 204 has no body */ }
  if (res.ok) return json;
  if (res.status === 429) throw new KbError('rate_limited', { retryAfter: Number(json?.retry_after) || null });
  if (res.status === 422 && json?.reason === 'description_too_long') throw new KbError('too_long');
  if (res.status === 422 && json?.reason === 'description_required') throw new KbError('required');
  if (res.status === 404 || res.status >= 500) throw new KbError('unavailable', { status: res.status });
  throw new KbError('rejected', { status: res.status, reason: json?.reason });
}

const locale = () => { try { return navigator.language || 'en-GB'; } catch { return 'en-GB'; } };

/** A: one tap. `helpful: false` is never followed by a wait for the gap. */
export const sendRating = ({ articleId, helpful, token }) => post(API_ENDPOINTS.KB_FEEDBACK, {
  article_id: articleId, helpful: Boolean(helpful), app: CLIENT_SURFACE, locale: locale(),
}, token);

/** B: editorial input, not support. No reply expected, no ticket raised. */
export const sendGap = ({ articleId, text, token }) => post(API_ENDPOINTS.KB_FEEDBACK_GAP, {
  article_id: articleId, text: String(text).trim().slice(0, SUPPORT_LIMITS.gapChars), app: CLIENT_SURFACE,
}, token);

/**
 * C: a ticket. Two fields from the user; account, organisation, user agent
 * and timestamp are attached server-side. The article's title and number and
 * the client version travel with the request (backend reply 19 Sep §2.2,
 * §2.3): the article set lives here, so the server has no title to attach.
 * No `reply_to` is ever sent (client response v4 §2.1).
 */
export const sendSupport = async ({ subject, description, articleId, articleTitle, articleNumber, token }) => {
  const json = await post(API_ENDPOINTS.KB_SUPPORT, {
    subject: String(subject).trim().replace(/[\r\n]+/g, ' ').slice(0, SUPPORT_LIMITS.subjectChars),
    description: String(description).trim(),
    article_id: articleId || null,
    ...(articleTitle ? { article_title: articleTitle } : {}),
    ...(articleNumber ? { article_number: articleNumber } : {}),
    app: CLIENT_SURFACE,
    client_version: CLIENT_VERSION,
  }, token);
  return {
    reference: json?.reference || json?.data?.reference || null,
    repliable: json?.repliable ?? json?.data?.repliable ?? null,
  };
};
